import { Gauge, ShieldCheck } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, SectionHeading } from "./Reveal";
import { STATS } from "../data/siteData";

const Badge = ({ children }) => (
  <span className="w-fit bg-kit/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-kit">
    {children}
  </span>
);

export const Stats = () => {
  const { lang, t } = useLanguage();
  const locale = lang === "es" ? "es-CL" : "en-US";
  const num = (n) => (n == null ? t.stats.table.noData : new Intl.NumberFormat(locale).format(n));

  return (
    <section id="estadisticas" className="scroll-mt-20 border-t border-ink-700/60 bg-ink-900/40 py-20 sm:py-28" data-testid="stats-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow={t.stats.eyebrow} title={t.stats.title} testId="stats-heading" />
        </Reveal>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Reveal delay={0.05} className="h-full">
            <div data-testid="stats-card-liga" className="flex h-full flex-col border border-ink-700/60 bg-ink-800 p-6 transition-colors duration-200 hover:border-kit/50">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-kit">
                {STATS.liga.competition[lang]} {STATS.liga.season}
              </p>
              <div className="mt-6 space-y-4">
                <div>
                  <p className="font-display text-5xl font-bold text-white" data-testid="stats-liga-matches">{STATS.liga.matches}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t.stats.matches}</p>
                </div>
                <div className="flex gap-8">
                  <div>
                    <p className="font-display text-2xl font-semibold text-white" data-testid="stats-liga-minutes">{num(STATS.liga.minutes)}</p>
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t.stats.minutes}</p>
                  </div>
                  <div>
                    <p className="font-display text-2xl font-semibold text-white" data-testid="stats-liga-goals">{STATS.liga.goals}</p>
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t.stats.goals}</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="h-full">
            <div data-testid="stats-card-copa" className="flex h-full flex-col border border-ink-700/60 bg-ink-800 p-6 transition-colors duration-200 hover:border-kit/50">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-kit">
                {STATS.copa.competition[lang]} {STATS.copa.season}
              </p>
              <div className="mt-6 space-y-4">
                <div>
                  <p className="font-display text-5xl font-bold text-white" data-testid="stats-copa-matches">{STATS.copa.matches}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t.stats.matches}</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-semibold text-white" data-testid="stats-copa-minutes">{num(STATS.copa.minutes)}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t.stats.minutes}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.19} className="h-full">
            <div data-testid="stats-card-defense" className="flex h-full flex-col border border-ink-700/60 bg-ink-800 p-6 transition-colors duration-200 hover:border-kit/50">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-kit">
                {t.stats.defenseTitle}
              </p>
              <div className="mt-6 flex-1 space-y-4">
                <div className="flex items-end gap-3">
                  <ShieldCheck size={22} className="mb-1 text-kit" />
                  <p className="font-display text-5xl font-bold text-white" data-testid="stats-interceptions">{STATS.defensive.interceptions}</p>
                </div>
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t.stats.interceptions}</p>
                <div>
                  <p className="font-display text-2xl font-semibold text-white" data-testid="stats-blocked-shots">{STATS.defensive.blockedShots}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-400">{t.stats.blockedShots}</p>
                </div>
              </div>
              <div className="mt-4">
                <Badge>{STATS.defensive.source[lang]}</Badge>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.26} className="h-full">
            <div data-testid="stats-card-speed" className="flex h-full flex-col border border-ink-700/60 bg-ink-800 p-6 transition-colors duration-200 hover:border-kit/50">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-kit">
                {t.stats.speedTitle}
              </p>
              <div className="mt-6 flex flex-1 items-end gap-3">
                <Gauge size={26} className="mb-2 text-kit" />
                <p className="font-display text-5xl font-bold text-white" data-testid="stats-top-speed">{t.stats.speedValue}</p>
              </div>
              <div className="mt-4">
                <Badge>{t.stats.agencyBadge}</Badge>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-12 border border-ink-700/60 bg-ink-900">
            <p className="border-b border-ink-700/60 px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
              {t.stats.tableTitle}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm" data-testid="stats-table">
                <thead>
                  <tr className="border-b border-ink-700/60 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <th className="px-5 py-3 font-semibold">{t.stats.table.competition}</th>
                    <th className="px-5 py-3 font-semibold">{t.stats.table.season}</th>
                    <th className="px-5 py-3 text-right font-semibold">{t.stats.table.matches}</th>
                    <th className="px-5 py-3 text-right font-semibold">{t.stats.table.minutes}</th>
                    <th className="px-5 py-3 text-right font-semibold">{t.stats.table.goals}</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-200">
                  <tr data-testid="stats-table-liga-ascenso" className="border-b border-ink-700/40 transition-colors duration-200 hover:bg-ink-800">
                    <td className="px-5 py-4 font-semibold text-white">{STATS.liga.competition[lang]}</td>
                    <td className="px-5 py-4">{STATS.liga.season}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-semibold text-kit">{STATS.liga.matches}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-semibold">{num(STATS.liga.minutes)}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-semibold">{STATS.liga.goals}</td>
                  </tr>
                  <tr data-testid="stats-table-copa-chile" className="border-b border-ink-700/40 transition-colors duration-200 hover:bg-ink-800">
                    <td className="px-5 py-4 font-semibold text-white">{STATS.copa.competition[lang]}</td>
                    <td className="px-5 py-4">{STATS.copa.season}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-semibold text-kit">{STATS.copa.matches}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-semibold">{num(STATS.copa.minutes)}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-semibold">{num(STATS.copa.goals)}</td>
                  </tr>
                  <tr data-testid="stats-table-total" className="bg-ink-800/60">
                    <td className="px-5 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-300" colSpan={2}>
                      {t.stats.table.total}
                    </td>
                    <td className="px-5 py-4 text-right font-display text-lg font-bold text-kit">{STATS.total2026.matches}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-bold text-white">{num(STATS.total2026.minutes)}</td>
                    <td className="px-5 py-4 text-right font-display text-lg font-bold text-white">{STATS.total2026.goals}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="border-t border-ink-700/60 px-5 py-3 text-xs text-zinc-400" data-testid="stats-career-note">
              {t.stats.careerLabel}:{" "}
              <span className="font-semibold text-zinc-200">
                {STATS.career.matches} {t.stats.table.matches} · {num(STATS.career.minutes)} {t.stats.table.minutes} · {STATS.career.goals} {t.stats.table.goals}
              </span>
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="mt-4 text-xs leading-relaxed text-zinc-500" data-testid="stats-source-note">
            {t.stats.noteCutoff}: {STATS.cutOff[lang]} · {t.stats.noteSource}: {STATS.source[lang]}.{" "}
            <a
              href={STATS.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-kit underline-offset-2 transition-colors duration-200 hover:text-white hover:underline"
              data-testid="stats-source-link"
            >
              {t.stats.noteLink}
            </a>
            . {t.stats.noteDisclaimer}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
